/**
 * Battle state on the client. Talks to the server over the socket, queues incoming states and plays each one's
 * events as short overlays (dice, badges, banners, dying cards) before committing the new board.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Action, GameEvent, GameView, MatchRewards, ServerMessage } from '@gooflord/shared';
import { ApiError, get } from '../lib/api';
import { socket } from '../lib/ws';
import { useStore } from '../store';
import { clockOffset, mergeToasts } from '../lib/util';
import { sfx } from './sfx';

export interface Seat { user_id: string | null; ai: boolean; team: number; seat: number; name: string; avatar: string }
export interface MatchInfo { id: string; mode: '1v1' | '2v2'; kind: string; status: 'waiting' | 'active' | 'finished' | 'cancelled'; seats: Seat[]; open: boolean; async: boolean; created_by: string | null; invited: string[]; turn_seconds: number }

export type Overlay =
  | { kind: 'roll'; value: number; need?: number; ok?: boolean; reason: string }
  | { kind: 'coins'; heads: number }
  | { kind: 'tame'; sum: number; need: number; ok: boolean; slug: string }
  | { kind: 'raid'; attackerUid: number | null; targetUid: number | null; ap: number; dp: number; result: 'kill' | 'die' | 'tie' }
  | { kind: 'banner'; text: string; sub?: string; tone?: 'good' | 'bad' | 'neutral' }
  | { kind: 'float'; uid: number; text: string; tone: 'good' | 'bad' };

/** The overlay on screen, with a per-step key (so consecutive banners restart their animation) and its duration (CSS matches it). */
export type ShownOverlay = Overlay & { key: number; ms: number };
interface Step { overlay: Overlay | null; ms: number; dying?: number[]; sound?: () => void }

const openError = (e: unknown) => {
  const st = e instanceof ApiError ? e.status : 0;
  return st === 403 ? 'Only friends of the players can watch this battle.' : st === 404 ? "That battle doesn't exist, or you can't see it." : (e as Error).message;
};

export function useArena(matchId: string) {
  const { user, notify, socketStatus } = useStore();
  const [match, setMatch] = useState<MatchInfo | null>(null);
  const [view, setView] = useState<GameView | null>(null);
  const [overlay, setOverlay] = useState<ShownOverlay | null>(null);
  const [dying, setDying] = useState<number[]>([]);
  const [result, setResult] = useState<MatchRewards | null>(null);
  const [emotes, setEmotes] = useState<{ id: number; player: number; emote: string }[]>([]);
  const [connected, setConnected] = useState<boolean[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [auto, setAutoState] = useState(false);
  const [toasts, setToasts] = useState<{ id: number; text: string }[]>([]);
  const queue = useRef<{ view: GameView; events: GameEvent[] }[]>([]);
  const processing = useRef(false);
  const lastLog = useRef(0);
  const viewRef = useRef<GameView | null>(null);
  const matchRef = useRef<MatchInfo | null>(null);
  const offset = useRef(0);            // server clock − ours, from `serverNow` on state messages
  const dead = useRef(false);          // unmounted: stop animating, ignore late replies
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const busyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ids = useRef(0);
  const reduced = !!user?.settings?.reduced_motion || (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches);
  const reducedRef = useRef(reduced); reducedRef.current = reduced; // the socket listener outlives renders
  const online = socketStatus === 'open';

  const later = (fn: () => void, ms: number) => { const t = setTimeout(() => { timers.current.delete(t); if (!dead.current) fn(); }, ms); timers.current.add(t); return t; };
  const stopBusy = () => { if (busyTimer.current) clearTimeout(busyTimer.current); busyTimer.current = null; setBusy(false); };

  const loadMatch = useCallback(async () => {
    try {
      const r = await get<{ match: MatchInfo; seat: number; view: GameView | null }>(`/api/matches/${matchId}`);
      if (dead.current) return null;
      matchRef.current = r.match; setMatch(r.match);
      if (r.match.status === 'finished' && r.view && !viewRef.current) { viewRef.current = r.view; setView(r.view); lastLog.current = r.view.log.at(-1)?.id ?? 0; }
      return r.match;
    } catch (e) { if (!dead.current && !matchRef.current) setError(openError(e)); return null; } // a blip while polling isn't fatal
  }, [matchId]);

  // per-match lifetime: everything below dies with this match (the Arena is keyed on matchId)
  useEffect(() => {
    dead.current = false;
    const ts = timers.current;
    return () => { dead.current = true; for (const t of ts) clearTimeout(t); ts.clear(); if (busyTimer.current) clearTimeout(busyTimer.current); busyTimer.current = null; };
  }, []);

  // lost the socket: whatever we sent may be gone; the fresh state after re-join is the truth
  useEffect(() => { if (!online) stopBusy(); }, [online]);

  // join the room whenever the socket (re)connects
  useEffect(() => {
    let joined = false;
    const off = socket.onStatus((s) => { if (s === 'open') { socket.send({ type: 'join', matchId }); joined = true; } });
    const offM = socket.on((m: ServerMessage) => {
      if ('matchId' in m && m.matchId !== matchId && m.type !== 'match_found') return;
      switch (m.type) {
        case 'state': offset.current = clockOffset(m.serverNow, Date.now()); queue.current.push({ view: m.view, events: m.events ?? [] }); pump(); break;
        case 'error': stopBusy(); if (m.message && !/Waiting for players/.test(m.message)) { notify(m.message, { kind: 'error' }); sfx.error(); } break;
        case 'presence': setConnected(m.connected); break;
        case 'emote': { const id = Date.now() + Math.random(); setEmotes((e) => [...e, { id, player: m.player, emote: m.emote }]); sfx.emote(); later(() => setEmotes((e) => e.filter((x) => x.id !== id)), 2600); break; }
        case 'result': setResult(m.rewards); break;
        case 'match_found': if (m.matchId === matchId) { socket.send({ type: 'join', matchId }); loadMatch(); } break;
      }
    });
    loadMatch().then((mt) => { if (mt?.status === 'active' && socket.status === 'open' && !joined) socket.send({ type: 'join', matchId }); });
    return () => { off(); offM(); if (socket.status === 'open') socket.send({ type: 'leave', matchId }); };
  }, [matchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // poll while waiting for players
  useEffect(() => {
    if (match?.status !== 'waiting') return;
    const t = setInterval(async () => { const mt = await loadMatch(); if (mt?.status === 'active') socket.send({ type: 'join', matchId }); }, 3000);
    return () => clearInterval(t);
  }, [match?.status, matchId, loadMatch]);

  const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, reducedRef.current ? 0 : ms)); // resolves even after unmount; pump checks `dead`

  function stepsFor(prev: GameView | null, next: GameView, events: GameEvent[]): Step[] {
    const me = next.me;
    const steps: Step[] = [];
    const name = (i: number) => next.players[i]?.name ?? '?';
    const uidOf = (v: GameView | null, p: number, slot: number) => v?.players[p]?.party[slot]?.uid ?? null;
    for (const e of events) {
      switch (e.kind) {
        case 'roll': steps.push({ overlay: { kind: 'roll', value: e.value, need: e.need, ok: e.ok, reason: e.reason }, ms: 1250, sound: sfx.dice }); break;
        case 'coins': steps.push({ overlay: { kind: 'coins', heads: e.heads }, ms: 500 + Math.min(e.heads + 1, 8) * 180, sound: sfx.coin }); break;
        case 'tame': steps.push({ overlay: { kind: 'tame', sum: e.sum, need: e.need, ok: e.ok, slug: e.slug }, ms: 1000, sound: () => sfx.tame(e.ok) }); break;
        case 'raid': steps.push({ overlay: { kind: 'raid', attackerUid: uidOf(prev, e.player, e.attacker), targetUid: uidOf(prev, e.targetPlayer, e.target), ap: e.ap, dp: e.dp, result: e.result }, ms: 900, sound: e.result === 'tie' ? sfx.tie : sfx.raid }); break;
        case 'raid_gooflord': steps.push({ overlay: { kind: 'banner', text: `${name(e.player)} raids the Gooflord`, tone: e.player === me ? 'good' : 'bad' }, ms: 900, sound: sfx.raid }); break;
        case 'kill': steps.push({ overlay: null, ms: 600, dying: [e.uid], sound: sfx.kill }); break;
        case 'sacrifice': steps.push({ overlay: null, ms: 550, dying: [e.uid], sound: sfx.draw }); break;
        case 'level': steps.push({ overlay: null, ms: 0, sound: sfx.level }); break; // the level ring bursts on its own
        case 'buff': steps.push({ overlay: { kind: 'float', uid: e.uid, text: `${e.delta > 0 ? '+' : ''}${e.delta}`, tone: e.delta > 0 ? 'good' : 'bad' }, ms: 650 }); break;
        case 'turn': if (!prev || prev.turn !== e.player || prev.turnNo !== e.turnNo) steps.push({ overlay: { kind: 'banner', text: e.player === me ? 'Your turn' : `${name(e.player)}'s turn`, sub: `turn ${e.turnNo}`, tone: e.player === me ? 'good' : 'neutral' }, ms: 850, sound: e.player === me ? sfx.turn : undefined }); break;
        case 'skip': steps.push({ overlay: { kind: 'banner', text: `${name(e.player)} skips a turn`, tone: e.player === me ? 'bad' : 'good' }, ms: 900 }); break;
        case 'extra_turn': steps.push({ overlay: { kind: 'banner', text: `Extra turn for ${name(e.player)}`, tone: e.player === me ? 'good' : 'bad' }, ms: 900 }); break;
        case 'forfeit': steps.push({ overlay: { kind: 'banner', text: `${name(e.player)} forfeits`, tone: 'neutral' }, ms: 900 }); break;
        case 'draw_fighter': case 'draw_item': if (e.player === me) steps.push({ overlay: null, ms: 0, sound: sfx.draw }); break;
        default: break;
      }
    }
    return steps;
  }

  async function pump() {
    if (processing.current) return;
    processing.current = true;
    try {
      while (queue.current.length && !dead.current) {
        const catchUp = queue.current.length > 2; // AI played several moves while we animated — hurry
        const { view: next, events } = queue.current.shift()!;
        const prev = viewRef.current;
        const steps = catchUp ? [] : stepsFor(prev, next, events);
        for (const s of steps) {
          if (dead.current) return;
          s.sound?.();
          if (s.dying?.length) setDying((d) => [...d, ...s.dying!]);
          setOverlay(s.overlay ? { ...s.overlay, key: ++ids.current, ms: s.ms } : null);
          await wait(s.ms);
        }
        if (dead.current) return;
        setOverlay(null);
        viewRef.current = next; setView(next); setDying([]); stopBusy();
        const fresh = next.log.filter((l) => l.id > lastLog.current);
        lastLog.current = next.log.at(-1)?.id ?? lastLog.current;
        if (prev && fresh.length) {
          const add = fresh.slice(-2).map((l) => ({ id: ++ids.current, text: l.text }));
          setToasts((t) => mergeToasts(t, add));
          later(() => setToasts((t) => t.filter((x) => !add.includes(x))), 2800);
        }
        if (next.winner !== null && prev?.winner === null) { const mine = next.players[next.me]?.team; if (mine != null) (next.winner === mine ? sfx.win : sfx.lose)(); loadMatch(); }
      }
    } finally { processing.current = false; }
  }

  // never queued while offline: the seq would be stale after reconnect and the server would answer "out of date"
  const act = useCallback((action: Action) => {
    const v = viewRef.current; if (!v) return;
    if (!socket.send({ type: 'act', matchId, seq: v.seq, action })) { notify('Reconnecting — try that again in a moment'); return; }
    setBusy(true);
    if (busyTimer.current) clearTimeout(busyTimer.current);
    busyTimer.current = setTimeout(() => { busyTimer.current = null; if (!dead.current) setBusy(false); }, 4000);
  }, [matchId, notify]);
  const forfeit = useCallback(() => socket.send({ type: 'forfeit', matchId }), [matchId]);
  const emote = useCallback((e: string) => socket.send({ type: 'emote', matchId, emote: e as never }), [matchId]);
  const setAuto = useCallback((on: boolean) => { setAutoState(on); socket.send({ type: 'auto', matchId, on }); }, [matchId]);

  const serverNow = useCallback(() => Date.now() + offset.current, []);

  return { match, view, overlay, dying, result, emotes, connected, error, busy, auto, toasts, reduced, online, socketStatus, serverNow, act, forfeit, emote, setAuto, reload: loadMatch, setResult };
}
